/**
 * HeyGen LiveAvatar controller (replaces the old Simli integration).
 *
 * For avatar mode the whole voice conversation IS the LiveAvatar session: LiveAvatar
 * connects a worker to our ElevenLabs ConvAI agent, handles the mic (voice chat), and
 * streams the avatar's audio+video into a single <video> element via `attach()`.
 * Because there is a single media element, there is exactly one audible voice and the
 * lips are synced by construction — no audio tapping/muting needed.
 *
 * The ElevenLabs `@elevenlabs/client` Conversation SDK is NOT used on this path.
 */
import {
  AgentEventsEnum,
  ElevenLabsAgentSession,
  SessionEvent,
  SessionInteractivityMode,
} from '@heygen/liveavatar-web-sdk';
import { escapeHTML } from '../../shared/utils/sanitize';
import { endLiveAvatarSessionAPI, startLiveAvatarSessionAPI } from './api';

export interface LiveAvatarElements {
  section: HTMLDivElement;
  video: HTMLVideoElement;
  loading: HTMLElement;
}

export interface StartAvatarOptions {
  apiUrl: string;
  uniqueId: string;
  els: LiveAvatarElements;
  status: HTMLParagraphElement;
  messagesContainer: HTMLDivElement;
  /** Called when the session ends on its own (e.g. server disconnect, max duration). */
  onEnded?: () => void;
}

let session: ElevenLabsAgentSession | null = null;
let running = false;
let settled = false;
let currentEls: LiveAvatarElements | null = null;
let onEndedCb: (() => void) | null = null;
let billing: { apiUrl: string; uniqueId: string } | null = null;
let sessionRef: string | null = null;
let startedAt = 0;

export function isAvatarRunning(): boolean {
  return running;
}

function newSessionRef(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  return `la-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

const TRANSCRIPT_PLACEHOLDER = 'Your conversation will appear here in real-time...';

function appendTranscript(container: HTMLDivElement, source: string, message: string): void {
  if (!message || !message.trim()) return;
  if ((container.textContent?.trim() || '') === TRANSCRIPT_PLACEHOLDER) container.innerHTML = '';
  const p = document.createElement('p');
  p.innerHTML = `<strong>${escapeHTML(source)}</strong> : ${escapeHTML(message)}`;
  container.appendChild(p);
  container.scrollTop = container.scrollHeight;
}

/**
 * Start a LiveAvatar session. Throws on failure (so the caller can surface credit /
 * permission errors the same way the plain voice path does).
 */
export async function startAvatarSession(opts: StartAvatarOptions): Promise<boolean> {
  const { apiUrl, uniqueId, els, status, messagesContainer, onEnded } = opts;
  try {
    settled = false;
    currentEls = els;
    onEndedCb = onEnded ?? null;
    billing = { apiUrl, uniqueId };
    sessionRef = newSessionRef();
    startedAt = 0;

    els.section.classList.remove('hidden');
    els.loading.classList.remove('hidden');
    status.textContent = 'Connecting...';

    // Upfront credit gate happens here (backend), then token is minted.
    const { sessionToken } = await startLiveAvatarSessionAPI(apiUrl, uniqueId);

    session = new ElevenLabsAgentSession(sessionToken, {
      voiceChat: { mode: SessionInteractivityMode.CONVERSATIONAL },
    });

    session.on(SessionEvent.SESSION_STREAM_READY, () => {
      // Attach ONLY once the stream is ready — the remote audio+video tracks exist now.
      // Attaching earlier logs "Stream is not yet ready" and the video stays black.
      try {
        session?.attach(els.video);
      } catch (attachErr) {
        console.warn('[witzo-voice] LiveAvatar attach failed:', attachErr);
      }
      els.loading.classList.add('hidden');
      status.textContent = 'listening';
      if (!startedAt) startedAt = Date.now();
      void els.video.play?.().catch(() => {});
    });

    session.on(SessionEvent.SESSION_DISCONNECTED, () => {
      void settle(true);
    });

    session.on(AgentEventsEnum.USER_TRANSCRIPTION, (e) => {
      appendTranscript(messagesContainer, 'user', e.text);
    });
    session.on(AgentEventsEnum.AVATAR_TRANSCRIPTION, (e) => {
      appendTranscript(messagesContainer, 'assistant', e.text);
    });
    session.on(AgentEventsEnum.AVATAR_SPEAK_STARTED, () => {
      status.textContent = 'speaking';
      els.section.classList.add('avatar-speaking');
    });
    session.on(AgentEventsEnum.AVATAR_SPEAK_ENDED, () => {
      status.textContent = 'listening';
      els.section.classList.remove('avatar-speaking');
    });

    await session.start();

    // NOTE: voice chat (mic) is auto-started from the constructor's `voiceChat` config —
    // calling voiceChat.start() again warns "Voice chat mode can only be set once", so we
    // don't. If we ever need explicit control, start it WITHOUT re-setting the mode.

    running = true;
    if (!startedAt) startedAt = Date.now();
    els.loading.classList.add('hidden');
    return true;
  } catch (err) {
    // Clean up UI/session but do NOT swallow — caller shows credit/permission errors.
    await settle(false);
    throw err;
  }
}

/** Send a typed user message to the ElevenLabs agent (equivalent to speaking it). */
export function sendAvatarUserMessage(text: string): void {
  const trimmed = text.trim();
  if (!trimmed || !session || !running) return;
  try {
    session.sendUserMessage(trimmed);
  } catch (err) {
    console.warn('[witzo-voice] Failed to send message to avatar agent:', err);
  }
}

/** Explicitly stop the session (user ended the call). */
export async function stopAvatarSession(): Promise<void> {
  await settle(false);
}

/**
 * Single teardown path: report duration for billing, stop the session, reset UI.
 * Guarded so an explicit stop and a server disconnect don't double-run.
 */
async function settle(notifyEnded: boolean): Promise<void> {
  if (settled) return;
  settled = true;
  running = false;

  const endedSession = session;
  session = null;

  // Bill by measured duration (idempotent per sessionRef on the backend).
  if (billing && sessionRef && startedAt) {
    const durationSecs = Math.max(0, Math.round((Date.now() - startedAt) / 1000));
    void endLiveAvatarSessionAPI(billing.apiUrl, billing.uniqueId, sessionRef, durationSecs);
  }
  billing = null;
  sessionRef = null;
  startedAt = 0;

  if (endedSession) {
    try {
      await endedSession.stop();
    } catch (err) {
      console.warn('[witzo-voice] Error stopping LiveAvatar session:', err);
    }
  }

  if (currentEls) {
    currentEls.section.classList.remove('avatar-speaking');
    currentEls.section.classList.add('hidden');
    currentEls.loading.classList.add('hidden');
    try {
      currentEls.video.srcObject = null;
    } catch {
      /* ignore */
    }
    currentEls = null;
  }

  const cb = onEndedCb;
  onEndedCb = null;
  if (notifyEnded && cb) cb();
}
